import {
  Box,
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  SimpleGrid,
  Text,
  useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthenticatedUserRO } from "@intentsify/dto";
import { LOCKOUT_TIME_IN_MINUTES } from "@intentsify/types";
import { pluralize } from "@intentsify/utils";
import { useMutation } from "@tanstack/react-query";
import { ResetPasswordScreenDefinition } from "auth";
import { getPostLoginRoute } from "auth/auth.utils";
import { consumeOAuthReturnPath } from "auth/oauthReturnPath";
import { Button } from "components";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaMicrosoft } from "react-icons/fa";
import { Navigate, useNavigate } from "react-router";
import { useUser } from "store/store.hooks";
import { useAppStore } from "store/useAppStore";
import { isCustomApiError } from "types";
import { handleApiError } from "utils";
import { z } from "zod";
import { signInRequest, ssoLookupRequest } from "./LoginForm.requests";
import { ResetPasswordLink } from "./ResetPasswordLink";

const emailSchema = z.object({
  email: z.email(),
});

const passwordSchema = z.object({
  password: z.string().min(8),
});

type EmailFields = z.infer<typeof emailSchema>;
type PasswordFields = z.infer<typeof passwordSchema>;

const LoginForm = () => {
  const [step, setStep] = useState<"email" | "sso" | "password">("email");
  const [emailValue, setEmailValue] = useState("");
  const [ssoCompanyId, setSsoCompanyId] = useState<number | null>(null);
  const [isSsoNavigating, setIsSsoNavigating] = useState(false);
  const [postLoginPath, setPostLoginPath] = useState<string>();

  const emailForm = useForm<EmailFields>({
    mode: "onSubmit",
    resolver: zodResolver(emailSchema),
  });

  const emailInput = emailForm.watch("email") ?? "";

  const passwordForm = useForm<PasswordFields>({
    mode: "onSubmit",
    resolver: zodResolver(passwordSchema),
  });

  const passwordInput = passwordForm.watch("password") ?? "";

  const user = useUser();
  const setUser = useAppStore.use.setUser();
  const navigate = useNavigate();
  const toast = useToast();

  const { mutate: lookupSso, isLoading: isLookingUp } = useMutation(
    ssoLookupRequest,
    {
      onSuccess: (result) => {
        if (result.provider && result.companyId) {
          setSsoCompanyId(result.companyId);
          setStep("sso");
        } else {
          setStep("password");
        }
      },
      onError: () => setStep("password"),
    },
  );

  const { mutate, isSuccess, isLoading } = useMutation(signInRequest, {
    onSuccess: ({ user }: AuthenticatedUserRO) => {
      setUser(user);
      setPostLoginPath(
        getPostLoginRoute(
          user,
          user.isActive ? consumeOAuthReturnPath() : undefined,
        ),
      );

      if (user.isActive) {
        toast.closeAll();
        toast({
          title: "Welcome back!",
          status: "success",
        });
      }
    },
    /**
     * We want to handle invalid credentials in a special way.
     * If it's not 401InvalidCredentials or 403AccountLockedOut let it be handled by handleApiError.
     */
    onError: (err) => {
      if (isCustomApiError(err)) {
        if (err.response.internalCode === "401InvalidCredentials") {
          toast({
            title: (
              <>
                Invalid email address or password. Did you forget your password?
                Click
                <Link
                  onClick={() => {
                    toast.closeAll();
                    navigate(ResetPasswordScreenDefinition.navigate());
                  }}
                  textDecoration="underline"
                >
                  {" "}
                  here{" "}
                </Link>
                to reset.
              </>
            ),
            status: "warning",
            isClosable: true,
            duration: 25 * 1000,
          });

          return;
        }

        if (err.response.internalCode === "403AccountLockedOut") {
          const unlockTime = err.response.metadata?.lockedOutTime
            ? DateTime.fromISO(
                String(err.response.metadata.lockedOutTime),
              ).plus({ minutes: LOCKOUT_TIME_IN_MINUTES })
            : null;
          const diff = unlockTime
            ? unlockTime.diffNow(["minutes"]).toObject()
            : null;

          toast({
            title: (
              <>
                <Text>{err.response.message}</Text>

                {diff && (
                  <Text>
                    Your account will be unlocked in{" "}
                    {Math.ceil(Number(diff.minutes))}{" "}
                    {pluralize(Math.ceil(Number(diff.minutes)), "minute")}.
                  </Text>
                )}
              </>
            ),
            status: "warning",
            isClosable: true,
            duration: 25 * 1000,
          });

          return;
        }
      }

      handleApiError(err, navigate, toast);
    },
    retry: false,
  });

  const handleNext = emailForm.handleSubmit(({ email }) => {
    setEmailValue(email);
    lookupSso(email);
  });

  const handlePasswordSubmit = passwordForm.handleSubmit(({ password }) => {
    mutate({ email: emailValue, password });
  });

  const handleSsoLogin = () => {
    if (!ssoCompanyId) return;
    setIsSsoNavigating(true);
    window.location.href = `${String(process.env.VITE_REACT_APP_API_URL)}/auth/sso/login?companyId=${ssoCompanyId}`;
  };

  useEffect(() => {
    if (isSuccess && !user?.isActive) {
      toast({
        title:
          "Before continuing you have to agree to the terms and conditions.",
        status: "info",
      });
    }
  }, [isSuccess, user?.isActive, toast]);

  if (isSuccess && user && postLoginPath) {
    return <Navigate to={postLoginPath} />;
  }

  return (
    <Flex justifyContent="center">
      <Flex direction="column" w="100%" gap={4}>
        <chakra.form onSubmit={handleNext} w="100%">
          <SimpleGrid columns={1} spacing={4}>
            <FormControl
              isInvalid={!!emailForm.formState.errors?.email?.message}
              isRequired
            >
              <Flex justify="space-between" align="center">
                <FormLabel color="white" mb={0}>
                  Email
                </FormLabel>
                {step !== "email" && !isLoading && (
                  <Link
                    color="gray.100"
                    fontSize="sm"
                    style={{ textDecoration: "none" }}
                    _hover={{ color: "green.200" }}
                    onClick={() => setStep("email")}
                  >
                    Edit
                  </Link>
                )}
              </Flex>
              <Input
                {...emailForm.register("email")}
                bg="gray.100"
                color="gray.600"
                _placeholder={{ color: "gray.400" }}
                type="email"
                placeholder="Email"
                isDisabled={step !== "email"}
              />
              <FormErrorMessage>
                {emailForm.formState.errors?.email?.message}
              </FormErrorMessage>
            </FormControl>

            {step === "email" && (
              <Button
                size="md"
                type="submit"
                fullWidth
                isLoading={isLookingUp}
                isDisabled={!emailInput || isLookingUp}
              >
                Next
              </Button>
            )}
          </SimpleGrid>
        </chakra.form>

        {step === "sso" && (
          <Button
            size="md"
            fullWidth
            leftIcon={<FaMicrosoft />}
            onClick={handleSsoLogin}
            isLoading={isSsoNavigating}
            isDisabled={isSsoNavigating}
          >
            Log in with Microsoft Entra ID
          </Button>
        )}

        {step === "password" && (
          <chakra.form onSubmit={handlePasswordSubmit} w="100%">
            <SimpleGrid columns={1} spacing={4}>
              <FormControl
                isInvalid={!!passwordForm.formState.errors?.password?.message}
                isRequired
              >
                <FormLabel color="white">Password</FormLabel>
                <Input
                  {...passwordForm.register("password")}
                  bg="gray.100"
                  color="gray.600"
                  _placeholder={{ color: "gray.400" }}
                  type="password"
                  placeholder="Password"
                />
                <FormErrorMessage>
                  {passwordForm.formState.errors?.password?.message}
                </FormErrorMessage>
              </FormControl>

              <Box>
                <ResetPasswordLink />
              </Box>

              <Button
                size="md"
                type="submit"
                fullWidth
                isLoading={isLoading}
                isDisabled={!passwordInput || isLoading}
              >
                Login
              </Button>
            </SimpleGrid>
          </chakra.form>
        )}
      </Flex>
    </Flex>
  );
};

export { LoginForm };
